import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AllderTable } from "../../../components/AllderTable";
import Breadcrumb from "../../../components/Breadcrumb";
import { useBreadcrumb } from "../../../hooks/useBreadcrumb";
import { useGlobalTable } from "../../../hooks/useGlobalTable";
import { reportHandler } from "../../../redux/handlers";

const INITIAL_PAGINATION = {
  defaultSort: "created_at",
  defaultOrder: "desc",
  defaultPageSize: 10,
};

export function DebtorReport() {
  const { businessSlug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const breadcrumbConfig = [
    { label: "รายงาน", path: "/" },
    { label: "รายงานลูกหนี้" },
  ];
  useBreadcrumb(breadcrumbConfig);

  const fetchDebtors = useCallback(
    (params) =>
      dispatch(
        reportHandler.getDebtorReport(businessSlug, {
          page: params.page || 1,
          limit: params.pageSize || INITIAL_PAGINATION.defaultPageSize,
          search: params.s || "",
          sortBy: params.sortBy || INITIAL_PAGINATION.defaultSort,
          sortOrder: params.sortOrder || INITIAL_PAGINATION.defaultOrder,
          perPage: params.per_page,
        })
      ),
    [businessSlug, dispatch]
  );

  const {
    data: debtors,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    filterParams,
  } = useGlobalTable({
    fetchData: fetchDebtors,
    ...INITIAL_PAGINATION,
  });

  const handleExport = async () => {
    try {
      await dispatch(
        reportHandler.getDebtorReportExcel(businessSlug, {
          s: filterParams.s || "",
        })
      );
    } catch (error) {
      console.error("Error exporting debtor report:", error);
    }
  };

  const handleNavigateToCustomer = useCallback(
    (customerId) => {
      navigate(`/${businessSlug}/report/debtor/customer/${customerId}`);
    },
    [businessSlug, navigate]
  );

  const formatDebtorData = useCallback((apiData) => {
    if (!apiData) return [];

    return apiData.map((item) => ({
      id: item.id,
      customerCode: item.code,
      customerName: `${item.first_name_th} ${item.last_name_th}`.trim(),
      customerPhoneNumber: item.phone_number || "-",
      creditLimit: item.memberGroup?.creditLimit || "-",
      debt: item.calculated_total_debt
        ? new Intl.NumberFormat("th-TH").format(item.calculated_total_debt)
        : "0",
      debtAmount: item.calculated_total_debt || 0,
    }));
  }, []);

  const columns = [
    {
      field: "customerCode",
      headerName: "รหัสสมาชิก",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <div
          style={{
            color: "#565DFF",
            fontWeight: 500,
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => handleNavigateToCustomer(row.id)}
        >
          {row.customerCode}
        </div>
      ),
    },
    {
      field: "customerName",
      headerName: "ชื่อลูกค้า",
      minWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => <div>{row.customerName}</div>,
    },
    {
      field: "customerPhoneNumber",
      headerName: "เบอร์โทรศัพท์",
      width: 120,
      sortable: false,
    },
    {
      field: "creditLimit",
      headerName: "วงเงินที่ได้รับ",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "debt",
      headerName: "ยอดหนี้สะสม",
      width: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => (
        <div
          className={`text-center ${
            row.debtAmount > 0 ? "text-[#FF0000]" : ""
          }`}
        >
          {row.debt}
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "จัดการ",
      width: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      type: "actions",
      renderCell: ({ row }) => (
        <IconButton
          aria-label="view customer details"
          onClick={() => handleNavigateToCustomer(row.id)}
        >
          <RemoveRedEyeIcon className="!text-[#F4852A]" />
        </IconButton>
      ),
    },
  ];

  const formattedData = formatDebtorData(debtors);

  return (
    <div>
      <Breadcrumb />

      <div className="card card-st-2">
        <div className="card-body px-0 py-4">
          <div className="text-lg font-bold mb-4 lh-1 px-4 mb-4 text-[#2DC76D]">
            รายงานลูกหนี้
          </div>

          <div className="d-flex align-items-center px-4 mb-4">
            <button
              className="btn btn-export-st-1 me-auto"
              onClick={handleExport}
            >
              Export
            </button>

            <div className="me-3">
              <TextField
                placeholder="ค้นหาชื่อลูกค้า"
                variant="outlined"
                size="small"
                className="form-control form-st-4"
                slotProps={{
                  input: {
                    sx: { borderRadius: "10px" },
                  },
                }}
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
          </div>

          <AllderTable
            rows={formattedData}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </div>
    </div>
  );
}
